import { useRef, useState, useEffect } from 'react'

interface UseCarouselProps {
	initialCardWidth?: number
}

interface UseCarouselReturn {
	sliderContainerRef: React.RefObject<HTMLDivElement>
	cardWidth: number
	isAtStart: boolean
	isAtEnd: boolean
	activeCard: number
	handleScroll: (direction: number) => void
	handleCardClick: (index: number) => void
}

export function useCarousel({ initialCardWidth = 435 }: UseCarouselProps = {}): UseCarouselReturn {
	const sliderContainerRef = useRef<HTMLDivElement>(null)
	const [cardWidth, setCardWidth] = useState<number>(initialCardWidth)
	const [isAtStart, setIsAtStart] = useState<boolean>(true)
	const [isAtEnd, setIsAtEnd] = useState<boolean>(false)
	const [activeCard, setActiveCard] = useState<number>(0)

	useEffect(() => {
		const container = sliderContainerRef.current
		if (!container) return

		const updateCardWidth = () => {
			const firstCard = container.querySelector('.carousel-card')
			if (firstCard) {
				const gap = window.innerWidth >= 768 ? 20 : 10
				setCardWidth(firstCard.clientWidth + gap)
			}
		}

		const handleScroll = () => {
			const { scrollLeft, scrollWidth, clientWidth } = container
			const offset = 1
			setIsAtStart(scrollLeft <= offset)
			const isAtEndPosition = Math.abs(scrollLeft + clientWidth - scrollWidth) <= offset
			setIsAtEnd(isAtEndPosition)
		}

		// Initial setup
		updateCardWidth()
		handleScroll()

		container.addEventListener('scroll', handleScroll)
		window.addEventListener('resize', () => {
			updateCardWidth()
			handleScroll() // Also check position on resize
		})

		return () => {
			container.removeEventListener('scroll', handleScroll)
			window.removeEventListener('resize', updateCardWidth)
		}
	}, [])

	const handleScroll = (direction: number) => {
		setActiveCard(activeCard + direction)

		if (sliderContainerRef.current) {
			sliderContainerRef.current.scrollBy({
				left: direction * cardWidth,
				behavior: 'smooth',
			})
		}
	}

	const handleCardClick = (index: number) => {
		if (index === activeCard) return
		setActiveCard(index)
		if (sliderContainerRef.current) {
			sliderContainerRef.current.scrollTo({
				left: index * cardWidth,
				behavior: 'smooth',
			})
		}
	}

	return {
		sliderContainerRef,
		cardWidth,
		isAtStart,
		isAtEnd,
		activeCard,
		handleScroll,
		handleCardClick,
	}
}
